import { MECHANIC_HELP_IDS } from './types.js';
export const EMPTY_GAME_CARD_UI = Object.freeze({});
const UI_KEYS = new Set([
    'mechanics', 'publicVars', 'statusProviders', 'effectStatusProviders',
    'planning', 'playback', 'capabilities', 'locationEffect',
]);
const LOCATION_EFFECT_KEYS = new Set([
    'symbol', 'label', 'description', 'counterFact', 'showCellBadge',
]);
const PROJECTION_KINDS = new Set(['number', 'boolean', 'presence']);
const MECHANICS = new Set(MECHANIC_HELP_IDS);
const PLAYBACK_KINDS = new Set([
    'creation', 'assembly', 'resource', 'timed', 'signal', 'hack',
    'reaction', 'virus', 'capture', 'attack', 'spell', 'phase',
]);
function nonEmpty(value, owner) {
    if (value.length === 0)
        throw new Error(`${owner} должен быть непустой строкой`);
}
function normalizeLabels(labels, owner) {
    if (labels === undefined)
        return undefined;
    const result = {};
    for (const [id, value] of Object.entries(labels)) {
        nonEmpty(id, `${owner} id`);
        nonEmpty(value.label, `${owner} "${id}" label`);
        result[id] = Object.freeze({
            label: value.label,
            ...(value.description === undefined ? {} : { description: value.description }),
        });
    }
    return Object.freeze(result);
}
export function normalizeGameCardUi(ui, owner) {
    if (ui === undefined)
        return EMPTY_GAME_CARD_UI;
    for (const key of Reflect.ownKeys(ui)) {
        if (!UI_KEYS.has(key))
            throw new Error(`${owner}: неизвестное ui поле "${String(key)}"`);
    }
    const mechanics = ui.mechanics === undefined ? undefined : [...ui.mechanics];
    if (mechanics !== undefined) {
        if (new Set(mechanics).size !== mechanics.length)
            throw new Error(`${owner}: ui.mechanics содержит дубликат`);
        for (const id of mechanics)
            if (!MECHANICS.has(id))
                throw new Error(`${owner}: неизвестная механика "${id}"`);
    }
    const publicVars = {};
    for (const [alias, projection] of Object.entries(ui.publicVars ?? {})) {
        nonEmpty(alias, `${owner} ui.publicVars alias`);
        nonEmpty(projection.key, `${owner} ui.publicVars "${alias}" key`);
        if (!PROJECTION_KINDS.has(projection.kind)) {
            throw new Error(`${owner}: ui.publicVars "${alias}" имеет неизвестный kind "${String(projection.kind)}"`);
        }
        publicVars[alias] = Object.freeze({ key: projection.key, kind: projection.kind });
    }
    const statusProviders = ui.statusProviders === undefined ? undefined : [...ui.statusProviders];
    const effectStatusProviders = ui.effectStatusProviders === undefined ? undefined : [...ui.effectStatusProviders];
    for (const provider of [...(statusProviders ?? []), ...(effectStatusProviders ?? [])]) {
        if (typeof provider !== 'function')
            throw new Error(`${owner}: ui status provider должен быть функцией`);
    }
    const stepLabels = normalizeLabels(ui.planning?.steps, `${owner} ui.planning.steps`);
    const modeLabels = normalizeLabels(ui.planning?.modes, `${owner} ui.planning.modes`);
    const planning = ui.planning === undefined ? undefined : Object.freeze({
        ...(stepLabels === undefined ? {} : { steps: stepLabels }),
        ...(modeLabels === undefined ? {} : { modes: modeLabels }),
    });
    const playback = ui.playback === undefined ? undefined : Object.freeze(Object.fromEntries(Object.entries(ui.playback).map(([eventType, label]) => {
        if (label === undefined)
            return [eventType, label];
        nonEmpty(label.label, `${owner} ui.playback "${eventType}" label`);
        if (label.kind !== undefined && !PLAYBACK_KINDS.has(label.kind)) {
            throw new Error(`${owner}: ui.playback "${eventType}" имеет неизвестный kind "${String(label.kind)}"`);
        }
        return [eventType, Object.freeze({
                label: label.label,
                ...(label.kind === undefined ? {} : { kind: label.kind }),
            })];
    })));
    if (ui.capabilities?.tracksTokenDeaths !== undefined
        && typeof ui.capabilities.tracksTokenDeaths !== 'boolean') {
        throw new Error(`${owner}: ui.capabilities.tracksTokenDeaths должен быть boolean`);
    }
    let locationEffect;
    if (ui.locationEffect !== undefined) {
        for (const key of Reflect.ownKeys(ui.locationEffect)) {
            if (!LOCATION_EFFECT_KEYS.has(key)) {
                throw new Error(`${owner}: неизвестное ui.locationEffect поле "${String(key)}"`);
            }
        }
        nonEmpty(ui.locationEffect.symbol, `${owner} ui.locationEffect.symbol`);
        nonEmpty(ui.locationEffect.label, `${owner} ui.locationEffect.label`);
        nonEmpty(ui.locationEffect.description, `${owner} ui.locationEffect.description`);
        if (ui.locationEffect.counterFact !== undefined) {
            nonEmpty(ui.locationEffect.counterFact, `${owner} ui.locationEffect.counterFact`);
            if (publicVars[ui.locationEffect.counterFact]?.kind !== 'number') {
                throw new Error(`${owner}: ui.locationEffect.counterFact должен ссылаться на number alias из ui.publicVars`);
            }
        }
        if (ui.locationEffect.showCellBadge !== undefined
            && typeof ui.locationEffect.showCellBadge !== 'boolean') {
            throw new Error(`${owner}: ui.locationEffect.showCellBadge должен быть boolean`);
        }
        if (ui.locationEffect.showCellBadge === true && ui.locationEffect.counterFact === undefined) {
            throw new Error(`${owner}: ui.locationEffect.showCellBadge требует counterFact`);
        }
        locationEffect = Object.freeze({ ...ui.locationEffect });
    }
    return Object.freeze({
        ...(mechanics === undefined ? {} : { mechanics: Object.freeze(mechanics) }),
        ...(Object.keys(publicVars).length === 0 ? {} : { publicVars: Object.freeze(publicVars) }),
        ...(statusProviders === undefined ? {} : { statusProviders: Object.freeze(statusProviders) }),
        ...(effectStatusProviders === undefined ? {} : { effectStatusProviders: Object.freeze(effectStatusProviders) }),
        ...(planning === undefined ? {} : { planning }),
        ...(playback === undefined ? {} : { playback }),
        ...(ui.capabilities === undefined ? {} : {
            capabilities: Object.freeze({
                ...(ui.capabilities.tracksTokenDeaths === undefined
                    ? {} : { tracksTokenDeaths: ui.capabilities.tracksTokenDeaths }),
            }),
        }),
        ...(locationEffect === undefined ? {} : { locationEffect }),
    });
}
//# sourceMappingURL=normalize.js.map